import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiPlus, FiTrash2, FiDownload, FiArrowLeft, FiEdit2, FiChevronRight, FiChevronDown, FiRefreshCw,
} from 'react-icons/fi';
import './FinanceProjectDetail.css';

const fmtBase = (v, c) => {
  try { return `${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${c || ''}`.trim(); }
  catch { return `${v} ${c || ''}`; }
};

const fmtMoney = (v) => {
  try { return Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }); }
  catch { return String(v ?? ''); }
};

const FinanceProjectDetail = ({ token, user }) => {
  const { fpId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProjectSettings, setEditingProjectSettings] = useState(false);
  const [settings, setSettings] = useState({
    profitPercent: 0,
    profitVisible: true,
    baseCurrency: 'USD',
    language: 'en',
    // Phase 1
    purpose: 'quotation',
    visibility: 'internal_only',
    syncMode: 'none',
    status: 'draft',
    supervisionMode: 'none',
    supervisionPercent: 0,
    supervisionManualAmount: 0,
  });

  // Phase 1: link board state
  const [linkBoardOpen, setLinkBoardOpen] = useState(false);
  const [linkBoardSearch, setLinkBoardSearch] = useState('');
  const [linkBoardChoice, setLinkBoardChoice] = useState(null);
  const [linkBoardSyncMode, setLinkBoardSyncMode] = useState('manual');
  const [linkBusy, setLinkBusy] = useState(false);
  const [boards, setBoards] = useState([]);
  const [syncBusy, setSyncBusy] = useState(false);
  const [liveSyncWarnOpen, setLiveSyncWarnOpen] = useState(false);

  // Item-level UI state
  const [expandedItems, setExpandedItems] = useState({}); // { [itemId]: true }
  const [collapsedGroups, setCollapsedGroups] = useState({}); // { [groupId]: true }
  const [kpiExpanded, setKpiExpanded] = useState(false);
  const [showClientPayments, setShowClientPayments] = useState(true);
  const [editingAdvanced, setEditingAdvanced] = useState(null);
  const [advancedDraft, setAdvancedDraft] = useState({});
  const [addItemOpen, setAddItemOpen] = useState(null);
  const [newItem, setNewItem] = useState({ name: '', costValue: 0, currency: 'USD', exchangeRate: 1 });
  // Group state
  const [addGroupOpen, setAddGroupOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', color: '#0F0F10' });
  const [editingGroup, setEditingGroup] = useState(null); // {id, name, color}
  // Share state (loaded from fp.clientShareEnabled / fp.clientShareToken)
  const [shareBusy, setShareBusy] = useState(false);
  const [copyOk, setCopyOk] = useState(false);
  // Members state (Finance Project access control)
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [memberDraft, setMemberDraft] = useState([]); // selected user ids
  const [membersBusy, setMembersBusy] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');

  // Payment modals
  const [subPaymentOpen, setSubPaymentOpen] = useState(null); // { itemId }
  const [subPaymentForm, setSubPaymentForm] = useState({});
  const [clientPaymentOpen, setClientPaymentOpen] = useState(false);
  const [clientPaymentForm, setClientPaymentForm] = useState({});
  const [editingSubPayment, setEditingSubPayment] = useState(null); // payment object

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchDetail = useCallback(async (showSpinner = false) => {
    try {
      if (showSpinner) setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/${fpId}`, { headers: authHeaders });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setSettings({
        profitPercent: json.financeProject.profitPercent || 0,
        profitVisible: json.financeProject.profitVisible !== false,
        baseCurrency: json.financeProject.baseCurrency || 'USD',
        language: json.financeProject.language || 'en',
        // Phase 1
        purpose: json.financeProject.purpose || 'quotation',
        visibility: json.financeProject.visibility || 'internal_only',
        syncMode: json.financeProject.syncMode || 'none',
        status: json.financeProject.status || 'draft',
        supervisionMode: json.financeProject.supervisionMode || 'none',
        supervisionPercent: json.financeProject.supervisionPercent ?? 0,
        supervisionManualAmount: json.financeProject.supervisionManualAmount ?? 0,
      });
    } catch (e) {
      console.error('Failed to load finance project', e);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [fpId, authHeaders]);

  useEffect(() => { setLoading(true); fetchDetail(true); }, [fetchDetail]);

  // Sync member draft whenever fresh project loads
  useEffect(() => {
    if (data?.financeProject?.allowedUserIds) {
      setMemberDraft(data.financeProject.allowedUserIds);
    } else {
      setMemberDraft([]);
    }
  }, [data?.financeProject?.id, data?.financeProject?.allowedUserIds]);

  const fetchWorkspaceUsers = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, { headers: authHeaders });
      const json = await res.json();
      setWorkspaceUsers(json.users || []);
    } catch (e) {
      console.error('Failed to load users', e);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (editingProjectSettings) {
      fetchWorkspaceUsers();
    }
  }, [editingProjectSettings, fetchWorkspaceUsers]);

  const saveSettings = async () => {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/${fpId}`, {
      method: 'PATCH',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        profitPercent: Number(settings.profitPercent) || 0,
        profitVisible: !!settings.profitVisible,
        baseCurrency: (settings.baseCurrency || 'USD').toUpperCase(),
        language: (settings.language || 'en').toLowerCase(),
        // Phase 1
        purpose: settings.purpose || 'quotation',
        visibility: settings.visibility || 'internal_only',
        syncMode: settings.syncMode || 'none',
        status: settings.status || 'draft',
        supervisionMode: settings.supervisionMode || 'none',
        supervisionPercent: Number(settings.supervisionPercent) || 0,
        supervisionManualAmount: Number(settings.supervisionManualAmount) || 0,
      }),
    });
    // Save members list if changed
    const fpUsers = data?.financeProject?.allowedUserIds || [];
    const a = new Set(fpUsers);
    const b = new Set(memberDraft);
    const sameMembers = a.size === b.size && [...a].every((id) => b.has(id));
    if (!sameMembers) {
      setMembersBusy(true);
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/${fpId}/members`, {
          method: 'PATCH',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ allowedUserIds: memberDraft }),
        });
      } catch (e) {
        console.error('Failed to update members', e);
      } finally {
        setMembersBusy(false);
      }
    }
    setEditingProjectSettings(false);
    fetchDetail();
  };

  const toggleMember = (uid) => {
    setMemberDraft((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  };

  // ---------- Phase 1: Board linking & sync ----------
  const fetchBoardsForLinking = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boards`, { headers: authHeaders });
      const json = await res.json();
      setBoards(json.boards || []);
    } catch (e) {
      console.error('Failed to load boards', e);
    }
  }, [authHeaders]);

  const openLinkBoard = () => {
    setLinkBoardSearch('');
    setLinkBoardChoice(null);
    setLinkBoardSyncMode('manual');
    setLinkBoardOpen(true);
    fetchBoardsForLinking();
  };

  const linkBoard = async () => {
    if (!linkBoardChoice) return;
    if (linkBoardSyncMode === 'live') {
      setLiveSyncWarnOpen(true);
      return;
    }
    setLinkBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/${fpId}/link-board`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ boardId: linkBoardChoice, syncMode: linkBoardSyncMode, runStructureSync: true }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to link board');
      } else {
        setLinkBoardOpen(false);
      }
      fetchDetail();
    } catch (e) {
      console.error(e);
    } finally {
      setLinkBusy(false);
    }
  };

  const confirmLiveSync = async () => {
    setLiveSyncWarnOpen(false);
    // Live sync isn't activated server-side until Batch B, but persist the choice.
    setLinkBusy(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/${fpId}/link-board`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ boardId: linkBoardChoice, syncMode: 'live', runStructureSync: true }),
      });
      setLinkBoardOpen(false);
      fetchDetail();
    } catch (e) {
      console.error(e);
    } finally {
      setLinkBusy(false);
    }
  };

  const unlinkBoard = async () => {
    if (!window.confirm('Unlink this finance project from its board? Finance items will remain.')) return;
    setLinkBusy(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/${fpId}/unlink-board`, {
        method: 'POST',
        headers: authHeaders,
      });
      fetchDetail();
    } catch (e) {
      console.error(e);
    } finally {
      setLinkBusy(false);
    }
  };

  const syncFromBoard = async () => {
    setSyncBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/${fpId}/sync-from-board`, {
        method: 'POST',
        headers: authHeaders,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(json.detail || 'Failed to sync from board');
      } else {
        const s = json.stats || {};
        alert(`Synced from board:\n+${s.createdItems || 0} new items, ~${s.updatedItems || 0} updated, +${s.createdGroups || 0} groups.`);
      }
      fetchDetail();
    } catch (e) {
      console.error(e);
    } finally {
      setSyncBusy(false);
    }
  };

  const pushToBoard = async () => {
    setSyncBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/${fpId}/push-to-board`, {
        method: 'POST',
        headers: authHeaders,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(json.detail || 'Failed to push to board');
      } else {
        const s = json.stats || {};
        alert(`Pushed client-visible values to board:\n${s.pushed || 0} items synced, ${s.skipped || 0} skipped.`);
      }
      fetchDetail();
    } catch (e) {
      console.error(e);
    } finally {
      setSyncBusy(false);
    }
  };

  const linkedBoard = useMemo(() => {
    if (!data?.financeProject?.linkedBoardId) return null;
    return boards.find((b) => b.id === data.financeProject.linkedBoardId) || null;
  }, [boards, data?.financeProject?.linkedBoardId]);

  // Preload boards once for the banner display (board name lookup)
  useEffect(() => {
    if (data?.financeProject?.linkedBoardId && boards.length === 0) {
      fetchBoardsForLinking();
    }
  }, [data?.financeProject?.linkedBoardId, boards.length, fetchBoardsForLinking]);
  // -----------------------------------------------------

  const patchItem = async (itemId, partial) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-items/${itemId}`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(partial),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to update task');
      }
      fetchDetail();
    } catch (e) {
      console.error(e);
    }
  };

  const deleteItem = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and its payments?`)) return;
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-items/${id}`, { method: 'DELETE', headers: authHeaders });
    fetchDetail();
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.name.trim()) return;
    const body = {
      financeProjectId: fpId,
      name: newItem.name.trim(),
      quantity: 1,
      costValue: Number(newItem.costValue) || 0,
      costCurrency: (newItem.currency || 'USD').toUpperCase(),
      costExchangeRate: Number(newItem.exchangeRate) || 1,
      sellValue: 0,
      sellCurrency: (newItem.currency || 'USD').toUpperCase(),
      sellExchangeRate: Number(newItem.exchangeRate) || 1,
    };
    if (addItemOpen && addItemOpen !== '_ungrouped') {
      body.financeGroupId = addItemOpen;
    }
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-items`, {
      method: 'POST',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setAddItemOpen(null);
    setNewItem({ name: '', costValue: 0, currency: 'USD', exchangeRate: 1 });
    fetchDetail();
  };

  const openSubPayment = (item) => {
    const fp = data?.financeProject;
    const pct = Number(fp?.profitPercent || 0) / 100;
    setSubPaymentOpen({ itemId: item.id, itemName: item.name });
    setSubPaymentForm({
      amount: 0,
      currency: item.costCurrency || fp?.baseCurrency || 'USD',
      exchangeRate: item.costExchangeRate || 1,
      clientEquivalentAmount: 0,
      clientEquivalentOverridden: false,
      profitPct: pct, // for live preview
      note: '',
      date: new Date().toISOString().slice(0, 10),
    });
  };

  const submitSubPayment = async (e) => {
    e.preventDefault();
    if (!subPaymentOpen) return;
    const amt = Number(subPaymentForm.amount) || 0;
    const body = {
      financeItemId: subPaymentOpen.itemId,
      kind: 'subcontractor',
      amount: amt,
      currency: (subPaymentForm.currency || 'USD').toUpperCase(),
      exchangeRate: Number(subPaymentForm.exchangeRate) || 1,
      date: subPaymentForm.date,
      note: subPaymentForm.note,
    };
    // Only pass clientEquivalentAmount if user explicitly overrode
    if (subPaymentForm.clientEquivalentOverridden) {
      body.clientEquivalentAmount = Number(subPaymentForm.clientEquivalentAmount) || 0;
      body.clientEquivalentOverridden = true;
    }
    const subRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-payments`, {
      method: 'POST',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!subRes.ok) {
      const err = await subRes.json().catch(() => ({}));
      alert(err.detail || 'Failed to record subcontractor payment');
      return;
    }
    setSubPaymentOpen(null);
    fetchDetail();
  };

  const startEditSubPayment = (p) => {
    const fp = data?.financeProject;
    const pct = Number(fp?.profitPercent || 0) / 100;
    setEditingSubPayment({
      id: p.id,
      amount: p.amount || 0,
      currency: p.currency || 'USD',
      exchangeRate: p.exchangeRate || 1,
      clientEquivalentAmount: p.clientEquivalentAmount || 0,
      clientEquivalentOverridden: !!p.clientEquivalentOverridden,
      profitPct: pct,
      date: p.date || '',
      note: p.note || '',
    });
  };

  const saveEditSubPayment = async (e) => {
    e.preventDefault();
    const body = {
      amount: Number(editingSubPayment.amount) || 0,
      currency: (editingSubPayment.currency || 'USD').toUpperCase(),
      exchangeRate: Number(editingSubPayment.exchangeRate) || 1,
      date: editingSubPayment.date,
      note: editingSubPayment.note,
    };
    if (editingSubPayment.clientEquivalentOverridden) {
      body.clientEquivalentAmount = Number(editingSubPayment.clientEquivalentAmount) || 0;
      body.clientEquivalentOverridden = true;
    } else {
      body.clientEquivalentOverridden = false;
    }
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-payments/${editingSubPayment.id}`, {
      method: 'PATCH',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || 'Failed to update payment');
    }
    setEditingSubPayment(null);
    fetchDetail();
  };

  const openClientPayment = () => {
    setClientPaymentOpen(true);
    setClientPaymentForm({
      amount: 0,
      currency: data?.financeProject?.baseCurrency || 'USD',
      exchangeRate: 1,
      note: '',
      date: new Date().toISOString().slice(0, 10),
    });
  };

  const submitClientPayment = async (e) => {
    e.preventDefault();
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-payments`, {
      method: 'POST',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        financeProjectId: fpId,
        kind: 'client',
        amount: Number(clientPaymentForm.amount) || 0,
        currency: (clientPaymentForm.currency || 'USD').toUpperCase(),
        exchangeRate: Number(clientPaymentForm.exchangeRate) || 1,
        date: clientPaymentForm.date,
        note: clientPaymentForm.note,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || 'Failed to record client payment');
      return;
    }
    setClientPaymentOpen(false);
    fetchDetail();
  };

  const deletePayment = async (id, force = false) => {
    if (!force && !window.confirm('Delete this payment?')) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/finance-payments/${id}${force ? '?force=true' : ''}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: authHeaders,
    });
    if (res.status === 409) {
      const body = await res.json().catch(() => ({}));
      const detail = body.detail || {};
      const confirmText =
        (detail.message || 'This deletion is destructive.') +
        (detail.actualAmount ? `\n\nActual contractor amount: ${detail.actualAmount}` : '') +
        (detail.clientEquivalentAmount ? `\nClient-visible amount: ${detail.clientEquivalentAmount}` : '') +
        '\n\nProceed anyway? Both Finance and the linked Board record will be removed.';
      if (window.confirm(confirmText)) {
        return deletePayment(id, true);
      }
      return;
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = err.detail && (err.detail.message || err.detail);
      alert(msg || 'Could not delete (only the creator can delete their payment).');
    }
    fetchDetail();
  };

  const openAdvancedEdit = (item) => {
    setEditingAdvanced(item.id);
    setAdvancedDraft({
      name: item.name,
      quantity: item.quantity || 1,
      unit: item.unit || '',
      costValue: item.costValue || 0,
      costCurrency: item.costCurrency,
      costExchangeRate: item.costExchangeRate || 1,
      sellValue: item.sellValue || 0,
      sellCurrency: item.sellCurrency,
      sellExchangeRate: item.sellExchangeRate || 1,
      sellOverridden: !!item.sellOverridden,
    });
  };

  const saveAdvancedEdit = async () => {
    await patchItem(editingAdvanced, {
      name: advancedDraft.name,
      quantity: Number(advancedDraft.quantity) || 1,
      unit: advancedDraft.unit || '',
      costValue: Number(advancedDraft.costValue) || 0,
      costCurrency: (advancedDraft.costCurrency || 'USD').toUpperCase(),
      costExchangeRate: Number(advancedDraft.costExchangeRate) || 1,
      sellValue: Number(advancedDraft.sellValue) || 0,
      sellCurrency: (advancedDraft.sellCurrency || 'USD').toUpperCase(),
      sellExchangeRate: Number(advancedDraft.sellExchangeRate) || 1,
      sellOverridden: !!advancedDraft.sellOverridden,
    });
    setEditingAdvanced(null);
  };

  const recomputeItemSell = async (itemId) => {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-items/${itemId}/recompute-sell`, {
      method: 'POST', headers: authHeaders,
    });
    fetchDetail();
  };

  const recomputeAllSell = async () => {
    if (!window.confirm('Recompute Sell from Cost for ALL tasks that are not manually locked?')) return;
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/${fpId}/recompute-sell`, {
      method: 'POST', headers: authHeaders,
    });
    const data = await res.json();
    alert(data.message || 'Done');
    fetchDetail();
  };

  const exportPdf = async () => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/${fpId}/quotation.pdf`, {
      headers: authHeaders,
    });
    if (!res.ok) {
      alert('Failed to generate quotation PDF');
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Quotation_${data?.financeProject?.name || 'project'}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ---- Groups ----
  const addGroup = async (e) => {
    e.preventDefault();
    if (!newGroup.name.trim()) return;
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-groups`, {
      method: 'POST',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        financeProjectId: fpId,
        name: newGroup.name.trim(),
        color: newGroup.color || '#0F0F10',
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || 'Failed to create group');
      return;
    }
    setAddGroupOpen(false);
    setNewGroup({ name: '', color: '#0F0F10' });
    fetchDetail();
  };

  const saveEditGroup = async () => {
    if (!editingGroup) return;
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-groups/${editingGroup.id}`, {
      method: 'PATCH',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: editingGroup.name, color: editingGroup.color }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || 'Failed to update group');
      return;
    }
    setEditingGroup(null);
    fetchDetail();
  };

  const deleteGroup = async (group) => {
    if (group.id === '_ungrouped') return;
    if (!window.confirm(`Delete group "${group.name}"? Tasks inside will be kept but moved to Ungrouped.`)) return;
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-groups/${group.id}`, {
      method: 'DELETE', headers: authHeaders,
    });
    fetchDetail();
  };

  // ---- Share link ----
  const toggleShare = async (enabled) => {
    setShareBusy(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/${fpId}/share`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      fetchDetail();
    } finally {
      setShareBusy(false);
    }
  };

  const rotateShare = async () => {
    if (!window.confirm('Generate a new link? The previous link will stop working.')) return;
    setShareBusy(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/${fpId}/share/rotate`, {
        method: 'POST', headers: authHeaders,
      });
      fetchDetail();
    } finally {
      setShareBusy(false);
    }
  };

  const copyShareLink = (url) => {
    try {
      navigator.clipboard.writeText(url);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1500);
    } catch {
      window.prompt('Copy this link:', url);
    }
  };

  if (loading) return <div className="fp-detail-loading" data-testid="fp-loading">Loading…</div>;
  if (!data) return <div className="fp-detail-empty" data-testid="fp-empty">Finance project not found.</div>;

  const fp = data.financeProject;
  const totals = data.totals;
  const base = fp.baseCurrency;
  const clientPayments = data.projectClientPayments || [];

  // Map items -> by group id
  const itemsByGroup = {};
  for (const it of (data.items || [])) {
    const gid = it.financeGroupId || '_ungrouped';
    (itemsByGroup[gid] = itemsByGroup[gid] || []).push(it);
  }

  const groupsList = (data.groups && data.groups.length > 0)
    ? data.groups
    : (data.items && data.items.length > 0
        ? [{ id: '_ungrouped', name: 'Tasks', color: '#B9B7AE', itemsCount: data.items.length,
             costBase: totals.costBase, sellBase: totals.sellBase, subcontractorPaidBase: totals.subcontractorPaidBase,
             subcontractorRemainingBase: totals.subcontractorRemainingBase, clientBilledBase: totals.clientBilledBase,
             clientBillableRemainingBase: 0, realizedProfitBase: totals.realizedProfitBase }]
        : []);

  return (
    <div className="fp-detail-page" data-testid="finance-project-detail">
      {/* Header */}
      <div className="fp-detail-header">
        <button className="fp-back-btn" onClick={() => navigate('/finance-projects')} data-testid="fp-back-btn">
          <FiArrowLeft size={16} /> Back
        </button>
        <div className="fp-detail-title-row">
          <h1 className="fp-detail-title">{fp.name}</h1>
          {fp.sourceBoardId && <span className="fp-card-pill" data-testid="fp-synced-pill">Synced from Board</span>}
        </div>
        {fp.client && <div className="fp-detail-client">Client: {fp.client}</div>}
        <div className="fp-detail-actions">
          <button className="btn btn-secondary" onClick={recomputeAllSell} data-testid="fp-recompute-all-btn"
            title="Reapply profit % to all non-locked task sell prices">
            <FiRefreshCw size={14} /> Recalculate Sell
          </button>
          <button className="btn btn-secondary" onClick={() => setEditingProjectSettings(true)} data-testid="fp-edit-settings-btn">
            <FiEdit2 size={14} /> Settings
          </button>
          <button className="btn btn-primary" onClick={exportPdf} data-testid="fp-export-pdf-btn">
            <FiDownload size={14} /> Export Quotation PDF
          </button>
        </div>
      </div>

      {/* Phase 1: Board Link & Sync Banner (Part 1 / Part 2 / Part 6) */}
      <div className="fp-link-banner" data-testid="fp-link-banner">
        <div className="fp-link-banner-badges">
          <span
            className={`fp-badge ${fp.linkedBoardId ? 'fp-badge-linked' : 'fp-badge-standalone'}`}
            data-testid="fp-banner-badge-link"
          >
            {fp.linkedBoardId ? (linkedBoard ? `Linked to: ${linkedBoard.name}` : 'Linked to Board') : 'Standalone'}
          </span>
          <span
            className={`fp-badge ${fp.visibility === 'client_visible' ? 'fp-badge-client' : 'fp-badge-internal'}`}
            data-testid="fp-banner-badge-visibility"
          >
            {fp.visibility === 'client_visible' ? 'Client Visible' : 'Internal Only'}
          </span>
          <span
            className={`fp-badge fp-badge-sync fp-badge-sync-${fp.syncMode || 'none'}`}
            data-testid="fp-banner-badge-sync"
          >
            {{ none: 'Sync: None', manual: 'Sync: Manual', live: 'Sync: Live' }[fp.syncMode || 'none']}
          </span>
          <span className="fp-badge fp-badge-purpose" data-testid="fp-banner-badge-purpose">
            {{
              quotation: 'Quotation',
              internal_costing: 'Internal Costing',
              execution_budget: 'Execution Budget',
              variation_order: 'Variation Order',
            }[fp.purpose] || 'Quotation'}
          </span>
          {fp.lastSyncedAt && (
            <span className="fp-badge fp-badge-muted" title={fp.lastSyncedAt} data-testid="fp-banner-last-synced">
              Last synced: {new Date(fp.lastSyncedAt).toLocaleString()}
            </span>
          )}
        </div>
        {fp.linkedBoardId && (
          <div className="fp-link-banner-note" data-testid="fp-supervision-note">
            Supervision is controlled by the linked Board Client Statement.
          </div>
        )}
        <div className="fp-link-banner-actions">
          {!fp.linkedBoardId ? (
            <button
              className="btn btn-secondary"
              onClick={openLinkBoard}
              disabled={linkBusy}
              data-testid="fp-link-board-btn"
            >
              Link to Board
            </button>
          ) : (
            <>
              <button
                className="btn btn-secondary"
                onClick={syncFromBoard}
                disabled={syncBusy}
                title="Pull task structure (groups, names, dates, status) from the linked board"
                data-testid="fp-sync-from-board-btn"
              >
                Sync Structure from Board
              </button>
              <button
                className="btn btn-secondary"
                onClick={pushToBoard}
                disabled={syncBusy}
                title="Push client-visible amounts back to the board's Client Statement (no internal cost/profit)"
                data-testid="fp-push-to-board-btn"
              >
                Push Client Values to Board
              </button>
              <button
                className="btn btn-secondary fp-btn-danger-outline"
                onClick={unlinkBoard}
                disabled={linkBusy}
                data-testid="fp-unlink-board-btn"
              >
                Unlink
              </button>
            </>
          )}
        </div>
      </div>

      {/* KPIs — 4 main buttons (2x2) with a single chevron expand revealing two-column details */}
      <div className="fp-kpis" data-testid="fp-kpis">
        <div className="fp-kpis-main">
          <KpiButton label="Cost" value={fmtBase(totals.costBase, base)} tone="sub" testId="kpi-cost" />
          <KpiButton label="Sell" value={fmtBase(totals.sellBase, base)} tone="cli" testId="kpi-sell" />
          <KpiButton label="Total Profit" value={fmtBase(totals.marginBase, base)} tone={totals.marginBase >= 0 ? 'pos' : 'neg'} testId="kpi-margin" />
          <KpiButton label="Cash Flow" value={fmtBase(totals.cashFlowBase, base)} tone={totals.cashFlowBase >= 0 ? 'pos' : 'neg'} testId="kpi-cashflow" />
        </div>
        <button type="button" className="fp-kpis-expand-btn"
          onClick={() => setKpiExpanded(!kpiExpanded)}
          aria-expanded={kpiExpanded}
          data-testid="fp-kpis-expand-btn">
          {kpiExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
          <span>{kpiExpanded ? 'Hide details' : 'Show details'}</span>
        </button>

        {kpiExpanded && (
          <div className="fp-kpis-details" data-testid="fp-kpis-details">
            <div className="fp-kpis-col fp-kpis-col-sub">
              <div className="fp-kpis-col-title">Subcontractor · Internal</div>
              <DetailLine label="Internal Cost" value={fmtBase(totals.costBase, base)} />
              <DetailLine label="Sub Paid" value={fmtBase(totals.subcontractorPaidBase, base)} />
              <DetailLine label="Sub Remaining" value={fmtBase(totals.subcontractorRemainingBase, base)} />
              <DetailLine label="Realized Profit" value={fmtBase(totals.realizedProfitBase, base)} hint="Client billed − Sub paid" highlight />
              <DetailLine label={`Expected Profit (${totals.profitPercent || 0}%)`} value={fmtBase(totals.expectedProfitBase, base)} hint="Cost × Profit %" />
            </div>
            <div className="fp-kpis-col fp-kpis-col-cli">
              <div className="fp-kpis-col-title">Client · External</div>
              <DetailLine label="Client Sell (Quotation)" value={fmtBase(totals.sellBase, base)} />
              <DetailLine label="Client Billed" value={fmtBase(totals.clientBilledBase, base)} hint="Work delivered" />
              <DetailLine label="Received Payments" value={fmtBase(totals.clientReceivedBase, base)} highlight />
              <DetailLine label="Client Debt" value={fmtBase(totals.clientOutstandingBase, base)} hint="Billed − Received" />
              <DetailLine label="Cash Flow" value={fmtBase(totals.cashFlowBase, base)} hint="Received − Sub paid" />
            </div>
          </div>
        )}
      </div>

      {/* PROJECT-LEVEL CLIENT PAYMENTS */}
      <div className="fp-client-payments-section" data-testid="fp-project-client-payments">
        <div className="fp-client-payments-header">
          <button type="button" className="fp-client-payments-toggle"
            onClick={() => setShowClientPayments(!showClientPayments)}>
            {showClientPayments ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
            <h3>Client Payments Received</h3>
            <span className="fp-cli-count">{clientPayments.length}</span>
          </button>
          <div className="fp-client-payments-stats">
            <span className="muted">Total received:</span> <strong>{fmtBase(totals.clientReceivedBase, base)}</strong>
            <button className="fp-pill-btn fp-pill-btn-client" onClick={openClientPayment} data-testid="fp-add-client-payment-btn">
              <FiPlus size={12} /> Record Client Payment
            </button>
          </div>
        </div>
        {showClientPayments && (
          clientPayments.length === 0 ? (
            <div className="fp-pay-empty">No client payments recorded yet. Click <strong>Record Client Payment</strong> to add one.</div>
          ) : (
            <div className="fp-client-payments-list">
              {clientPayments.map((p) => (
                <div className="fp-client-payment-row" key={p.id} data-testid={`fp-client-pay-${p.id}`}>
                  <div className="fp-pay-amount">{fmtMoney(p.amount)} <span className="fp-money-ccy">{p.currency}</span></div>
                  <div className="fp-pay-date">{p.date}</div>
                  <div className="fp-pay-creator">
                    <span className="fp-pay-creator-dot" style={{ background: p.createdByColor || '#0F0F10' }} />
                    {p.createdByName || '—'}
                  </div>
                  <div className="fp-pay-note" title={p.note || ''}>{p.note || ''}</div>
                  <div className="fp-pay-actions">
                    {(p.createdBy === user?.id || user?.role === 'Admin') && (
                      <button className="icon-btn icon-btn-danger" onClick={() => deletePayment(p.id)}
                        title="Delete payment" data-testid={`fp-del-cli-pay-${p.id}`}>
                        <FiTrash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Tasks */}
      <div className="fp-items-section">
        <div className="fp-items-header">
          <h3>Tasks ({(data.items || []).length})</h3>
          <button
            className="btn btn-primary"
            onClick={() => setAddGroupOpen(true)}
            data-testid="fp-add-group-btn"
          >
            <FiPlus size={14} /> Add Group
          </button>
        </div>

        {(data.items || []).length === 0 && groupsList.length === 0 ? (
          <div className="empty-state">No tasks yet. Add a group and a task, or import from a board.</div>
        ) : (
          <div className="fp-groups">
            {groupsList.map((g) => {
              const gItems = itemsByGroup[g.id] || [];
              const collapsed = !!collapsedGroups[g.id];
              return (
                <div className="fp-group" key={g.id} data-testid={`fp-group-${g.id}`}>
                  <div
                    className="fp-group-header"
                    onClick={() => setCollapsedGroups({ ...collapsedGroups, [g.id]: !collapsed })}
                    style={{ borderLeftColor: g.color || '#B9B7AE' }}
                  >
                    <div className="fp-group-header-left">
                      {collapsed ? <FiChevronRight size={16} /> : <FiChevronDown size={16} />}
                      <span className="fp-group-color-dot" style={{ background: g.color || '#B9B7AE' }} />
                      <span className="fp-group-name">{g.name}</span>
                      <span className="fp-group-count">{gItems.length} task{gItems.length === 1 ? '' : 's'}</span>
                      {g.id !== '_ungrouped' && (
                        <>
                          <button
                            type="button"
                            className="icon-btn icon-btn-xs"
                            onClick={(e) => { e.stopPropagation(); setEditingGroup({ id: g.id, name: g.name, color: g.color || '#0F0F10' }); }}
                            title="Rename group"
                            data-testid={`fp-group-edit-${g.id}`}
                          >
                            <FiEdit2 size={11} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn icon-btn-xs icon-btn-danger"
                            onClick={(e) => { e.stopPropagation(); deleteGroup(g); }}
                            title="Delete group"
                            data-testid={`fp-group-del-${g.id}`}
                          >
                            <FiTrash2 size={11} />
                          </button>
                        </>
                      )}
                    </div>
                    <div className="fp-group-header-right">
                      <span className="fp-group-subtotal">
                        <span className="muted">Cost</span> {fmtBase(g.costBase, base)}
                      </span>
                      <span className="fp-group-subtotal">
                        <span className="muted">Sell</span> {fmtBase(g.sellBase, base)}
                      </span>
                      <span className="fp-group-subtotal">
                        <span className="muted">Billed</span> {fmtBase(g.clientBilledBase, base)}
                      </span>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={(e) => { e.stopPropagation(); setAddItemOpen(g.id); }}
                        data-testid={`fp-add-item-${g.id}`}
                      >
                        <FiPlus size={12} /> Add Task
                      </button>
                    </div>
                  </div>

                  {!collapsed && (
                    <div className="fp-items-table">
                      <div className="fp-items-table-head">
                        <div></div>
                        <div>Task</div>
                        <div className="text-right">Cost ({base})</div>
                        <div className="text-right">Sell ({base})</div>
                        <div className="text-right">Actions</div>
                      </div>
                      {gItems.length === 0 && (
                        <div className="fp-group-empty">No tasks in this group yet.</div>
                      )}
                      {gItems.map((it) => (
                        <FinanceItemRow
                          key={it.id}
                          item={it}
                          base={base}
                          profitPercent={totals.profitPercent}
                          expanded={!!expandedItems[it.id]}
                          onToggleExpand={() => setExpandedItems({ ...expandedItems, [it.id]: !expandedItems[it.id] })}
                          onPatch={patchItem}
                          onDelete={deleteItem}
                          onAddSubPayment={openSubPayment}
                          onEditSubPayment={startEditSubPayment}
                          onDeletePayment={deletePayment}
                          onAdvancedEdit={openAdvancedEdit}
                          onRecomputeSell={recomputeItemSell}
                          currentUserId={user?.id}
                          currentUserRole={user?.role}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Phase 1: Link Board modal */}
      {linkBoardOpen && (
        <div className="modal-overlay" onClick={() => setLinkBoardOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="fp-link-board-modal">
            <h2>Link to Board</h2>
            <p className="fp-form-hint">
              Linking enables structure sync (Board → Finance) and the ability to push client-visible amounts to the Board's
              Client Statement. Internal cost and profit remain private and are never synced to the Board.
            </p>
            <div className="form-row">
              <input
                className="input"
                type="text"
                placeholder="Search boards…"
                value={linkBoardSearch}
                onChange={(e) => setLinkBoardSearch(e.target.value)}
                data-testid="fp-link-board-search"
              />
            </div>
            <div
              className="fp-board-picker"
              style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border-light, #e5e5e5)', borderRadius: 6, padding: 6, marginBottom: 12 }}
              data-testid="fp-link-board-list"
            >
              {boards.length === 0 ? (
                <div className="empty-state" style={{ padding: 8, opacity: 0.7 }}>Loading boards…</div>
              ) : (
                boards
                  .filter((b) => {
                    const q = linkBoardSearch.trim().toLowerCase();
                    if (!q) return true;
                    return (b.name || '').toLowerCase().includes(q);
                  })
                  .map((b) => (
                    <label
                      key={b.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', borderRadius: 4 }}
                      data-testid={`fp-link-board-option-${b.id}`}
                    >
                      <input
                        type="radio"
                        name="link-board-choice"
                        checked={linkBoardChoice === b.id}
                        onChange={() => setLinkBoardChoice(b.id)}
                      />
                      <span style={{ flex: 1 }}>{b.name}</span>
                      {b.client && <span style={{ fontSize: 12, opacity: 0.6 }}>{b.client}</span>}
                    </label>
                  ))
              )}
            </div>
            <div className="form-row">
              <label>Sync Mode</label>
              <select
                className="input"
                value={linkBoardSyncMode}
                onChange={(e) => setLinkBoardSyncMode(e.target.value)}
                data-testid="fp-link-board-sync-mode"
              >
                <option value="manual">Manual — sync only when you click the buttons</option>
                <option value="live">Live — auto-sync changes (requires confirmation)</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setLinkBoardOpen(false)} data-testid="fp-link-board-cancel">
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={linkBoard}
                disabled={!linkBoardChoice || linkBusy}
                data-testid="fp-link-board-confirm"
              >
                {linkBusy ? 'Linking…' : 'Link & Sync Structure'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 1: Live Sync warning */}
      {liveSyncWarnOpen && (
        <div className="modal-overlay" onClick={() => setLiveSyncWarnOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="fp-live-sync-warn">
            <h2>Enable Live Sync?</h2>
            <p className="fp-form-hint" style={{ marginBottom: 12 }}>
              <strong>Live Sync</strong> automatically synchronises structure changes from the Board into this Finance Project,
              and pushes client-visible amounts back to the Board's Client Statement whenever pricing changes.
            </p>
            <p className="fp-form-hint" style={{ marginBottom: 12 }}>
              Note: live sync is engineered to <em>never</em> overwrite internal cost or profit, but you should confirm you
              understand that changes on either side can affect the other.
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setLiveSyncWarnOpen(false)} data-testid="fp-live-sync-cancel">
                Cancel
              </button>
              <button className="btn btn-primary" onClick={confirmLiveSync} data-testid="fp-live-sync-confirm">
                Enable Live Sync
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {editingProjectSettings && (
        <div className="modal-overlay" onClick={() => setEditingProjectSettings(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Project Settings</h2>
            <div className="form-row">
              <label>Base Currency</label>
              <input className="input" value={settings.baseCurrency} maxLength={4}
                onChange={(e) => setSettings({ ...settings, baseCurrency: e.target.value.toUpperCase() })}
                data-testid="fp-settings-base-currency" />
            </div>
            <div className="form-row">
              <label>Quotation / Client Portal Language</label>
              <select
                className="input"
                value={settings.language}
                onChange={(e) => setSettings({ ...settings, language: e.target.value })}
                data-testid="fp-settings-language"
              >
                <option value="en">English</option>
                <option value="ar">العربية (Arabic)</option>
                <option value="tr">Türkçe (Turkish)</option>
              </select>
            </div>
            <div className="form-row">
              <label>Profit % (on Cost)</label>
              <input className="input" type="number" step="0.01" value={settings.profitPercent}
                onChange={(e) => setSettings({ ...settings, profitPercent: e.target.value })}
                data-testid="fp-settings-profit-percent" />
            </div>
            <div className="form-row form-row-checkbox">
              <label>
                <input type="checkbox" checked={settings.profitVisible}
                  onChange={(e) => setSettings({ ...settings, profitVisible: e.target.checked })} />
                <span>Show as separate line in quotation (otherwise distribute into each task)</span>
              </label>
            </div>
            <p className="fp-form-hint">
              Changing Profit % will automatically recompute Sell for every task that isn&apos;t manually locked.
            </p>

            {/* Phase 1: Purpose / Visibility / Status (Part 1) */}
            <hr className="fp-settings-divider" />
            <h3 className="fp-settings-subhead">Project Type & Visibility</h3>
            <div className="form-row">
              <label>Purpose</label>
              <select
                className="input"
                value={settings.purpose}
                onChange={(e) => setSettings({ ...settings, purpose: e.target.value })}
                data-testid="fp-settings-purpose"
              >
                <option value="quotation">Quotation</option>
                <option value="internal_costing">Internal Costing</option>
                <option value="execution_budget">Execution Budget</option>
                <option value="variation_order">Variation Order</option>
              </select>
            </div>
            <div className="form-row">
              <label>Visibility</label>
              <select
                className="input"
                value={settings.visibility}
                onChange={(e) => setSettings({ ...settings, visibility: e.target.value })}
                data-testid="fp-settings-visibility"
              >
                <option value="internal_only">Internal Only — not shared with client</option>
                <option value="client_visible">Client Visible — exposed via Client Statement / public link</option>
              </select>
            </div>
            <div className="form-row">
              <label>Status</label>
              <select
                className="input"
                value={settings.status}
                onChange={(e) => setSettings({ ...settings, status: e.target.value })}
                data-testid="fp-settings-status"
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Phase 1: Supervision (Part 3) -- DISABLED FOR LINKED PROJECTS (Phase 1b).
                Supervision authority is the Board for linked projects. */}
            <hr className="fp-settings-divider" />
            <h3 className="fp-settings-subhead">Supervision</h3>
            {fp.linkedBoardId ? (
              <div className="fp-form-hint" style={{
                padding: '10px 12px',
                background: 'rgba(33, 150, 243, 0.08)',
                border: '1px solid rgba(33, 150, 243, 0.25)',
                borderRadius: 8,
                color: '#1976d2',
                marginBottom: 12,
              }} data-testid="fp-supervision-locked-note">
                <strong>Supervision is controlled by the linked Board Client Statement.</strong><br/>
                Set the supervision percentage in the Board's Client Statement settings. Any value here
                is ignored for linked projects so the client sees a single supervision rate.
              </div>
            ) : (
              <>
                <p className="fp-form-hint">
                  Supervision is a separate fee shown to the client on top of the client-visible amounts.
                  Only a visible percentage is supported — no hidden supervision behavior.
                </p>
                <div className="form-row">
                  <label>Mode</label>
                  <select
                    className="input"
                    value={settings.supervisionMode}
                    onChange={(e) => setSettings({ ...settings, supervisionMode: e.target.value })}
                    data-testid="fp-settings-supervision-mode"
                  >
                    <option value="none">None (0%)</option>
                    <option value="visible_percent">Visible % — separate line</option>
                  </select>
                </div>
                {settings.supervisionMode === 'visible_percent' && (
                  <div className="form-row">
                    <label>Supervision %</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input"
                      value={settings.supervisionPercent}
                      onChange={(e) => setSettings({ ...settings, supervisionPercent: e.target.value })}
                      data-testid="fp-settings-supervision-percent"
                    />
                  </div>
                )}
              </>
            )}

            {/* Members / Access control */}
            {(user?.role === 'Admin' || user?.role === 'PM') && (
              <>
                <hr className="fp-settings-divider" />
                <h3 className="fp-settings-subhead">Project Members</h3>
                <p className="fp-form-hint">
                  Only Admins, the creator, and the members listed below can see and open this Finance Project.
                  Other team members will not see it in their list.
                </p>
                <div className="form-row">
                  <input
                    className="input"
                    type="text"
                    placeholder="Search members by name or email…"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    data-testid="fp-members-search"
                  />
                </div>
                <div
                  className="fp-members-list"
                  data-testid="fp-members-list"
                  style={{
                    maxHeight: 220,
                    overflowY: 'auto',
                    border: '1px solid var(--border-light, #e5e5e5)',
                    borderRadius: 6,
                    padding: 6,
                    marginBottom: 12,
                  }}
                >
                  {workspaceUsers.length === 0 ? (
                    <div className="empty-state" style={{ padding: 8, fontSize: 13, opacity: 0.7 }}>Loading members…</div>
                  ) : (
                    workspaceUsers
                      .filter((u) => {
                        const q = memberSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (
                          (u.name || '').toLowerCase().includes(q) ||
                          (u.email || '').toLowerCase().includes(q)
                        );
                      })
                      .map((u) => {
                        const isCreator = data?.financeProject?.createdBy === u.id;
                        const isAdmin = u.role === 'Admin';
                        const isMe = user?.id === u.id;
                        const checked = memberDraft.includes(u.id) || isCreator || isAdmin;
                        const disabled = isCreator || isAdmin; // implicit access
                        return (
                          <label
                            key={u.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '6px 8px',
                              borderRadius: 4,
                              cursor: disabled ? 'not-allowed' : 'pointer',
                              opacity: disabled ? 0.75 : 1,
                            }}
                            data-testid={`fp-member-row-${u.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={disabled || membersBusy}
                              onChange={() => toggleMember(u.id)}
                              data-testid={`fp-member-toggle-${u.id}`}
                            />
                            <span style={{ flex: 1 }}>
                              <span style={{ fontWeight: 500 }}>{u.name || u.email}{isMe ? ' (you)' : ''}</span>
                              <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.7 }}>{u.email}</span>
                            </span>
                            <span style={{ fontSize: 11, opacity: 0.6 }}>
                              {isAdmin ? 'Admin · always' : isCreator ? 'Creator · always' : u.role || ''}
                            </span>
                          </label>
                        );
                      })
                  )}
                </div>
              </>
            )}

            {/* Share link section */}
            <hr className="fp-settings-divider" />
            <h3 className="fp-settings-subhead">Client Share Link</h3>
            {(() => {
              const url = (fp.clientShareEnabled && fp.clientShareToken)
                ? `${window.location.origin}/client-finance/${fp.clientShareToken}`
                : '';
              return (
                <>
                  <div className="form-row form-row-checkbox">
                    <label>
                      <input type="checkbox" checked={!!fp.clientShareEnabled}
                        disabled={shareBusy}
                        onChange={(e) => toggleShare(e.target.checked)}
                        data-testid="fp-share-toggle" />
                      <span>Enable read-only client view (no login required)</span>
                    </label>
                  </div>
                  {fp.clientShareEnabled && fp.clientShareToken ? (
                    <>
                      <div className="fp-share-link-box">
                        <input className="input fp-share-link-input" type="text" readOnly value={url}
                          data-testid="fp-share-link-input"
                          onFocus={(e) => e.target.select()} />
                        <button type="button" className="btn btn-secondary btn-sm"
                          onClick={() => copyShareLink(url)} data-testid="fp-share-copy-btn">
                          {copyOk ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <div className="fp-share-actions">
                        <a className="fp-share-preview-link" href={url} target="_blank" rel="noopener noreferrer"
                          data-testid="fp-share-preview-link">
                          Preview the client view →
                        </a>
                        <button type="button" className="fp-share-rotate-link"
                          onClick={rotateShare} disabled={shareBusy}
                          data-testid="fp-share-rotate-btn">
                          Regenerate link
                        </button>
                      </div>
                      <p className="fp-form-hint">
                        Clients see only their statement (totals, items by group, billed-so-far, payments).
                        Internal cost, profit %, and subcontractor data are never exposed.
                      </p>
                    </>
                  ) : (
                    <p className="fp-form-hint">Enable to generate a unique link you can share with the client.</p>
                  )}
                </>
              );
            })()}

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditingProjectSettings(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveSettings} data-testid="fp-settings-save">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Group modal */}
      {addGroupOpen && (
        <div className="modal-overlay" onClick={() => setAddGroupOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add Group</h2>
            <form onSubmit={addGroup}>
              <div className="form-row">
                <label>Group Name *</label>
                <input className="input" autoFocus value={newGroup.name}
                  onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  placeholder="e.g. Furniture, Mechanical, Electrical"
                  required data-testid="fp-new-group-name" />
              </div>
              <div className="form-row">
                <label>Color</label>
                <div className="fp-color-picker" data-testid="fp-new-group-color">
                  {['#0F0F10', '#1E5FCC', '#1DBF73', '#E24B5B', '#F4A340', '#8B5CF6', '#D7FF1F', '#F472B6'].map((c) => (
                    <button type="button" key={c}
                      className={`fp-color-swatch ${newGroup.color === c ? 'is-active' : ''}`}
                      style={{ background: c }}
                      onClick={() => setNewGroup({ ...newGroup, color: c })}
                      data-testid={`fp-new-group-color-${c.replace('#','')}`}
                      aria-label={`Color ${c}`} />
                  ))}
                  <input type="color" className="fp-color-input" value={newGroup.color || '#0F0F10'}
                    onChange={(e) => setNewGroup({ ...newGroup, color: e.target.value })} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setAddGroupOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="fp-new-group-submit">Add Group</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Group modal */}
      {editingGroup && (
        <div className="modal-overlay" onClick={() => setEditingGroup(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Group</h2>
            <div className="form-row">
              <label>Group Name</label>
              <input className="input" autoFocus value={editingGroup.name}
                onChange={(e) => setEditingGroup({ ...editingGroup, name: e.target.value })}
                data-testid="fp-edit-group-name" />
            </div>
            <div className="form-row">
              <label>Color</label>
              <div className="fp-color-picker">
                {['#0F0F10', '#1E5FCC', '#1DBF73', '#E24B5B', '#F4A340', '#8B5CF6', '#D7FF1F', '#F472B6'].map((c) => (
                  <button type="button" key={c}
                    className={`fp-color-swatch ${editingGroup.color === c ? 'is-active' : ''}`}
                    style={{ background: c }}
                    onClick={() => setEditingGroup({ ...editingGroup, color: c })}
                    aria-label={`Color ${c}`} />
                ))}
                <input type="color" className="fp-color-input" value={editingGroup.color || '#0F0F10'}
                  onChange={(e) => setEditingGroup({ ...editingGroup, color: e.target.value })} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditingGroup(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEditGroup} data-testid="fp-edit-group-save">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Item modal */}
      {addItemOpen && (
        <div className="modal-overlay" onClick={() => setAddItemOpen(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add Task</h2>
            <form onSubmit={addItem}>
              <div className="form-row">
                <label>Task Name *</label>
                <input className="input" autoFocus value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  required data-testid="fp-new-item-name" />
              </div>
              <div className="form-grid-2">
                <div className="form-row">
                  <label>Cost (Internal)</label>
                  <input className="input" type="number" step="0.01" value={newItem.costValue}
                    onChange={(e) => setNewItem({ ...newItem, costValue: e.target.value })}
                    data-testid="fp-new-item-cost" />
                </div>
                <div className="form-row">
                  <label>Currency</label>
                  <input className="input" value={newItem.currency} maxLength={4}
                    onChange={(e) => setNewItem({ ...newItem, currency: e.target.value.toUpperCase() })} />
                </div>
              </div>
              <div className="form-row">
                <label>Exchange Rate (1 {base} = X {newItem.currency})</label>
                <input className="input" type="number" step="0.0001" value={newItem.exchangeRate}
                  onChange={(e) => setNewItem({ ...newItem, exchangeRate: e.target.value })} />
              </div>
              <p className="fp-form-hint">
                Sell price will be auto-calculated: <strong>{fmtMoney((Number(newItem.costValue) || 0) * (1 + (Number(fp.profitPercent || 0) / 100)))} {newItem.currency}</strong> ({fp.profitPercent || 0}% profit). You can edit it later.
              </p>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setAddItemOpen(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="fp-new-item-submit">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Advanced edit modal */}
      {editingAdvanced && (
        <div className="modal-overlay" onClick={() => setEditingAdvanced(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Task</h2>
            <div className="form-row">
              <label>Task Name</label>
              <input className="input" value={advancedDraft.name}
                onChange={(e) => setAdvancedDraft({ ...advancedDraft, name: e.target.value })} />
            </div>
            <div className="form-grid-2">
              <div className="form-row">
                <label>Quantity</label>
                <input className="input" type="number" step="0.01" value={advancedDraft.quantity}
                  onChange={(e) => setAdvancedDraft({ ...advancedDraft, quantity: e.target.value })} />
              </div>
              <div className="form-row">
                <label>Unit</label>
                <input className="input" value={advancedDraft.unit || ''}
                  onChange={(e) => setAdvancedDraft({ ...advancedDraft, unit: e.target.value })} />
              </div>
            </div>
            <div className="form-grid-2">
              <div className="form-row">
                <label>Cost Value</label>
                <input className="input" type="number" step="0.01" value={advancedDraft.costValue}
                  onChange={(e) => setAdvancedDraft({ ...advancedDraft, costValue: e.target.value })} />
              </div>
              <div className="form-row">
                <label>Cost Currency</label>
                <input className="input" value={advancedDraft.costCurrency || ''} maxLength={4}
                  onChange={(e) => setAdvancedDraft({ ...advancedDraft, costCurrency: e.target.value.toUpperCase() })} />
              </div>
            </div>
            <div className="form-row">
              <label>Cost Exchange Rate (1 {base} = X {advancedDraft.costCurrency})</label>
              <input className="input" type="number" step="0.0001" value={advancedDraft.costExchangeRate}
                onChange={(e) => setAdvancedDraft({ ...advancedDraft, costExchangeRate: e.target.value })} />
            </div>
            <div className="form-grid-2">
              <div className="form-row">
                <label>Sell Value</label>
                <input className="input" type="number" step="0.01" value={advancedDraft.sellValue}
                  onChange={(e) => setAdvancedDraft({ ...advancedDraft, sellValue: e.target.value, sellOverridden: true })} />
              </div>
              <div className="form-row">
                <label>Sell Currency</label>
                <input className="input" value={advancedDraft.sellCurrency || ''} maxLength={4}
                  onChange={(e) => setAdvancedDraft({ ...advancedDraft, sellCurrency: e.target.value.toUpperCase() })} />
              </div>
            </div>
            <div className="form-row">
              <label>Sell Exchange Rate (1 {base} = X {advancedDraft.sellCurrency})</label>
              <input className="input" type="number" step="0.0001" value={advancedDraft.sellExchangeRate}
                onChange={(e) => setAdvancedDraft({ ...advancedDraft, sellExchangeRate: e.target.value })} />
            </div>
            <div className="form-row form-row-checkbox">
              <label>
                <input type="checkbox" checked={!!advancedDraft.sellOverridden}
                  onChange={(e) => setAdvancedDraft({ ...advancedDraft, sellOverridden: e.target.checked })} />
                <span>Lock sell price (don&apos;t auto-recalculate from cost &amp; profit %)</span>
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditingAdvanced(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveAdvancedEdit} data-testid="fp-advanced-save">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Subcontractor Payment modal (Add) */}
      {subPaymentOpen && (
        <PaymentModalSub
          title={`Subcontractor Payment · ${subPaymentOpen.itemName}`}
          form={subPaymentForm}
          setForm={setSubPaymentForm}
          base={base}
          onClose={() => setSubPaymentOpen(null)}
          onSubmit={submitSubPayment}
          submitTestId="fp-sub-payment-submit"
        />
      )}

      {/* Subcontractor Payment modal (Edit) */}
      {editingSubPayment && (
        <PaymentModalSub
          title="Edit Subcontractor Payment"
          form={editingSubPayment}
          setForm={setEditingSubPayment}
          base={base}
          onClose={() => setEditingSubPayment(null)}
          onSubmit={saveEditSubPayment}
          submitTestId="fp-sub-payment-save"
        />
      )}

      {/* Client Payment modal */}
      {clientPaymentOpen && (
        <div className="modal-overlay" onClick={() => setClientPaymentOpen(false)}>
          <div className="modal fp-payment-modal fp-payment-modal-client" onClick={(e) => e.stopPropagation()}>
            <h2>Record Client Payment (Incoming)</h2>
            <p className="fp-form-hint">
              This payment is recorded at the <strong>project level</strong> — it&apos;s not attached to any specific task.
            </p>
            <form onSubmit={submitClientPayment}>
              <div className="form-grid-2">
                <div className="form-row">
                  <label>Amount *</label>
                  <input className="input" type="number" step="0.01" autoFocus value={clientPaymentForm.amount}
                    onChange={(e) => setClientPaymentForm({ ...clientPaymentForm, amount: e.target.value })} required
                    data-testid="fp-cli-pay-amount" />
                </div>
                <div className="form-row">
                  <label>Currency</label>
                  <input className="input" value={clientPaymentForm.currency} maxLength={4}
                    onChange={(e) => setClientPaymentForm({ ...clientPaymentForm, currency: e.target.value.toUpperCase() })} />
                </div>
              </div>
              <div className="form-grid-2">
                <div className="form-row">
                  <label>Exchange Rate (1 {base} = X {clientPaymentForm.currency})</label>
                  <input className="input" type="number" step="0.0001" value={clientPaymentForm.exchangeRate}
                    onChange={(e) => setClientPaymentForm({ ...clientPaymentForm, exchangeRate: e.target.value })} />
                </div>
                <div className="form-row">
                  <label>Date</label>
                  <input className="input" type="date" value={clientPaymentForm.date}
                    onChange={(e) => setClientPaymentForm({ ...clientPaymentForm, date: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <label>Note (optional)</label>
                <input className="input" value={clientPaymentForm.note}
                  onChange={(e) => setClientPaymentForm({ ...clientPaymentForm, note: e.target.value })}
                  placeholder="e.g. First installment, Bank transfer ref…" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setClientPaymentOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="fp-cli-pay-submit">Record</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== Sub-payment Modal (Add/Edit) ==============
const PaymentModalSub = ({ title, form, setForm, base, onClose, onSubmit, submitTestId }) => {
  const pct = Number(form.profitPct || 0);
  const derivedClient = (Number(form.amount) || 0) * (1 + pct);
  const showClient = form.clientEquivalentOverridden
    ? Number(form.clientEquivalentAmount || 0)
    : derivedClient;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal fp-payment-modal fp-payment-modal-subcontractor" onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        <p className="fp-form-hint">
          The amount you actually paid the subcontractor. The Client-Equivalent is the matching amount that will be billed to the client (auto = paid × (1 + profit %)).
        </p>
        <form onSubmit={onSubmit}>
          <div className="form-grid-2">
            <div className="form-row">
              <label>Amount Paid *</label>
              <input className="input" type="number" step="0.01" autoFocus value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} required
                data-testid="fp-sub-pay-amount" />
            </div>
            <div className="form-row">
              <label>Currency</label>
              <input className="input" value={form.currency} maxLength={4}
                onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} />
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-row">
              <label>Exchange Rate (1 {base} = X {form.currency})</label>
              <input className="input" type="number" step="0.0001" value={form.exchangeRate}
                onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })} />
            </div>
            <div className="form-row">
              <label>Date</label>
              <input className="input" type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>
          <div className="fp-client-equiv-box">
            <div className="fp-client-equiv-header">
              <span>Client-Equivalent (billed to client)</span>
              <label className="fp-client-equiv-toggle">
                <input type="checkbox" checked={!!form.clientEquivalentOverridden}
                  onChange={(e) => setForm({
                    ...form,
                    clientEquivalentOverridden: e.target.checked,
                    clientEquivalentAmount: e.target.checked
                      ? (Number(form.clientEquivalentAmount) || derivedClient)
                      : derivedClient,
                  })} />
                <span>Override manually</span>
              </label>
            </div>
            <div className="fp-client-equiv-input">
              <input className="input" type="number" step="0.01"
                value={form.clientEquivalentOverridden ? form.clientEquivalentAmount : Number(derivedClient.toFixed(2))}
                disabled={!form.clientEquivalentOverridden}
                onChange={(e) => setForm({ ...form, clientEquivalentAmount: e.target.value })}
                data-testid="fp-sub-pay-client-equiv" />
              <span className="fp-money-ccy">{form.currency}</span>
            </div>
            <div className="fp-client-equiv-note">
              {form.clientEquivalentOverridden
                ? <>Manually set: <strong>{fmtMoney(showClient)} {form.currency}</strong></>
                : <>Auto: <strong>{fmtMoney(form.amount)} × (1 + {(pct * 100).toFixed(2)}%) = {fmtMoney(showClient)} {form.currency}</strong></>}
            </div>
          </div>
          <div className="form-row">
            <label>Note (optional)</label>
            <input className="input" value={form.note || ''}
              onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" data-testid={submitTestId}>Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const KPI = ({ label, value, sub, tone, testId }) => (
  <div className={`fp-kpi-card fp-kpi-${tone || ''}`} data-testid={testId}>
    <div className="fp-kpi-label">{label}</div>
    <div className="fp-kpi-value">{value}</div>
    {sub && <div className="fp-kpi-sub">{sub}</div>}
  </div>
);

const KpiButton = ({ label, value, tone, testId }) => (
  <div className={`fp-kpi-btn fp-kpi-btn-${tone || ''}`} data-testid={testId}>
    <div className="fp-kpi-btn-label">{label}</div>
    <div className="fp-kpi-btn-value">{value}</div>
  </div>
);

const DetailLine = ({ label, value, hint, highlight }) => (
  <div className={`fp-detail-line ${highlight ? 'fp-detail-line-highlight' : ''}`}>
    <div className="fp-detail-line-label">
      {label}
      {hint && <span className="fp-detail-line-hint"> · {hint}</span>}
    </div>
    <div className="fp-detail-line-value">{value}</div>
  </div>
);

// --- Inline-editable cell for cost / sell values ---
const InlineMoneyCell = ({ value, ccy, baseValue, base, onCommit, ariaLabel, locked, dataTestId, emptyDash }) => {
  const [editing, setEditing] = useState(false);
  const isEmpty = emptyDash && (value === null || value === undefined);
  const [draft, setDraft] = useState(isEmpty ? '' : String(value ?? 0));
  const [lastValue, setLastValue] = useState(value);
  const inputRef = useRef(null);

  if (!editing && lastValue !== value) {
    setLastValue(value);
    setDraft((emptyDash && (value === null || value === undefined)) ? '' : String(value ?? 0));
  }

  const commit = () => {
    setEditing(false);
    if (emptyDash && draft.trim() === '') {
      // Allow clearing to null
      if (value !== null && value !== undefined) onCommit(null);
      return;
    }
    const num = Number(draft);
    if (Number.isNaN(num)) {
      setDraft(isEmpty ? '' : String(value ?? 0));
      return;
    }
    if (num === Number(value ?? 0) && !isEmpty) return;
    onCommit(num);
  };

  return (
    <div className={`fp-money-cell ${editing ? 'fp-money-cell-editing' : ''}`}>
      {editing ? (
        <input
          ref={inputRef}
          autoFocus
          className="fp-money-input"
          type="number"
          step="0.01"
          value={draft}
          aria-label={ariaLabel}
          placeholder={emptyDash ? '—' : ''}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            else if (e.key === 'Escape') { setDraft(isEmpty ? '' : String(value ?? 0)); setEditing(false); }
          }}
          data-testid={dataTestId}
        />
      ) : (
        <button
          type="button"
          className={`fp-money-display ${isEmpty ? 'fp-money-display-empty' : ''}`}
          onClick={() => setEditing(true)}
          title={locked ? 'Click to edit (sell is currently locked / overridden)' : (isEmpty ? 'Click to enter' : 'Click to edit')}
          data-testid={dataTestId}
        >
          {isEmpty ? (
            <span className="fp-money-value fp-money-empty">—</span>
          ) : (
            <>
              <span className="fp-money-value">{fmtMoney(value)} <span className="fp-money-ccy">{ccy || ''}</span></span>
              <span className="fp-money-base">≈ {fmtBase(baseValue, base)}</span>
            </>
          )}
          {locked && !isEmpty && <span className="fp-money-locked">locked</span>}
        </button>
      )}
    </div>
  );
};

const FinanceItemRow = ({
  item, base, profitPercent, expanded, onToggleExpand, onPatch, onDelete,
  onAddSubPayment, onEditSubPayment, onDeletePayment, onAdvancedEdit, onRecomputeSell,
  currentUserId, currentUserRole,
}) => {
  const [nameDraft, setNameDraft] = useState(item.name);
  const [editingName, setEditingName] = useState(false);
  const [lastName, setLastName] = useState(item.name);

  if (!editingName && lastName !== item.name) {
    setLastName(item.name);
    setNameDraft(item.name);
  }

  const subPayments = item.subcontractorPayments || [];

  const commitName = () => {
    setEditingName(false);
    if (nameDraft.trim() && nameDraft !== item.name) {
      onPatch(item.id, { name: nameDraft.trim() });
    } else {
      setNameDraft(item.name);
    }
  };

  return (
    <>
      <div className="fp-item-row" data-testid={`fp-item-row-${item.id}`}>
        <button
          type="button"
          className="fp-expand-btn"
          onClick={onToggleExpand}
          title={expanded ? 'Hide subcontractor payments' : 'Show subcontractor payments'}
          aria-expanded={expanded}
          data-testid={`fp-expand-${item.id}`}
        >
          {expanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
        </button>

        <div className="fp-item-name-cell">
          {editingName ? (
            <input
              className="fp-money-input fp-name-input"
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitName(); }
                else if (e.key === 'Escape') { setNameDraft(item.name); setEditingName(false); }
              }}
              data-testid={`fp-name-input-${item.id}`}
            />
          ) : (
            <button
              type="button"
              className="fp-item-name-btn"
              onClick={() => setEditingName(true)}
              title="Click to edit name"
              data-testid={`fp-name-display-${item.id}`}
            >
              <span className="fp-item-name">{item.name}</span>
              {item.sourceItemId && <span className="fp-item-sync-tag">from board</span>}
              {item.unit && <span className="fp-item-unit">{item.unit}</span>}
              {(item.quantity && Number(item.quantity) !== 1) && (
                <span className="fp-item-qty">×{item.quantity}</span>
              )}
            </button>
          )}
        </div>

        <div className="text-right">
          <InlineMoneyCell
            value={item.costValue}
            ccy={item.costCurrency}
            baseValue={item.costBase}
            base={base}
            ariaLabel="Cost"
            emptyDash
            onCommit={(v) => onPatch(item.id, { costValue: v, internalCost: v })}
            dataTestId={`fp-cost-${item.id}`}
          />
        </div>

        <div className="text-right">
          <InlineMoneyCell
            value={item.sellValue}
            ccy={item.sellCurrency}
            baseValue={item.sellBase}
            base={base}
            ariaLabel="Sell"
            locked={!!item.sellOverridden}
            onCommit={(v) => onPatch(item.id, { sellValue: v, sellOverridden: true })}
            dataTestId={`fp-sell-${item.id}`}
          />
        </div>

        <div className="text-right">
          <div className="fp-row-actions">
            <button
              type="button"
              className="fp-pill-btn fp-pill-btn-sub"
              onClick={() => onAddSubPayment(item)}
              title="Add a payment you made to the subcontractor"
              data-testid={`fp-add-sub-${item.id}`}
            >
              <FiPlus size={12} /> Sub Pay
            </button>
            {item.sellOverridden && (
              <button
                type="button"
                className="icon-btn"
                onClick={() => onRecomputeSell(item.id)}
                title="Reapply profit % to recompute sell from cost"
                data-testid={`fp-recompute-${item.id}`}
              >
                <FiRefreshCw size={13} />
              </button>
            )}
            <button
              type="button"
              className="icon-btn"
              onClick={() => onAdvancedEdit(item)}
              title="Advanced edit (quantity, currency, etc.)"
              data-testid={`fp-edit-adv-${item.id}`}
            >
              <FiEdit2 size={13} />
            </button>
            <button
              type="button"
              className="icon-btn icon-btn-danger"
              onClick={() => onDelete(item.id, item.name)}
              title="Delete task"
              data-testid={`fp-delete-${item.id}`}
            >
              <FiTrash2 size={13} />
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="fp-payments-expand" data-testid={`fp-payments-${item.id}`}>
          {/* Phase 1: Pricing summary & mode selector (Part 3) */}
          <div className="fp-pricing-block" data-testid={`fp-pricing-${item.id}`}>
            <div className="fp-pay-block-header">
              <div className="fp-pay-block-title">Pricing</div>
              <div className="fp-pricing-mode-row">
                <label className="fp-pricing-mode-label">Mode</label>
                <select
                  className="input fp-pricing-mode-select"
                  value={item.pricingMode || 'profit_percent'}
                  onChange={(e) => onPatch(item.id, { pricingMode: e.target.value })}
                  data-testid={`fp-pricing-mode-${item.id}`}
                >
                  <option value="profit_percent">Profit % on Cost</option>
                  <option value="manual_profit_amount">Manual Profit Amount</option>
                  <option value="manual_client_price">Manual Client Price</option>
                  <option value="no_profit">No Profit (cost = price)</option>
                </select>
                {item.pricingMode === 'profit_percent' && (
                  <span className="fp-pricing-inline-input">
                    <span className="muted">Profit</span>
                    <input
                      type="number"
                      step="0.01"
                      className="input fp-mini-input"
                      defaultValue={item.profitPercent ?? 0}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (!Number.isNaN(v) && v !== (item.profitPercent ?? 0)) {
                          onPatch(item.id, { profitPercent: v });
                        }
                      }}
                      data-testid={`fp-pricing-profit-pct-${item.id}`}
                    />
                    <span className="muted">%</span>
                  </span>
                )}
                {item.pricingMode === 'manual_profit_amount' && (
                  <span className="fp-pricing-inline-input">
                    <span className="muted">Profit amount</span>
                    <input
                      type="number"
                      step="0.01"
                      className="input fp-mini-input"
                      defaultValue={item.manualProfitAmount ?? 0}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (!Number.isNaN(v) && v !== (item.manualProfitAmount ?? 0)) {
                          onPatch(item.id, { manualProfitAmount: v });
                        }
                      }}
                      data-testid={`fp-pricing-manual-profit-${item.id}`}
                    />
                  </span>
                )}
                {item.pricingMode === 'manual_client_price' && (
                  <span className="fp-pricing-inline-input">
                    <span className="muted">Client price</span>
                    <input
                      type="number"
                      step="0.01"
                      className="input fp-mini-input"
                      defaultValue={item.manualClientPrice ?? 0}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (!Number.isNaN(v) && v !== (item.manualClientPrice ?? 0)) {
                          onPatch(item.id, { manualClientPrice: v });
                        }
                      }}
                      data-testid={`fp-pricing-manual-price-${item.id}`}
                    />
                  </span>
                )}
              </div>
            </div>
            <div className="fp-pricing-grid">
              <div className="fp-pricing-cell">
                <div className="fp-pricing-cell-label">Internal Cost</div>
                <div className="fp-pricing-cell-value" data-testid={`fp-pricing-internal-${item.id}`}>
                  {fmtBase(item.internalCost, item.internalCurrency || base)}
                </div>
              </div>
              <div className="fp-pricing-cell">
                <div className="fp-pricing-cell-label">Profit</div>
                <div className="fp-pricing-cell-value" data-testid={`fp-pricing-profit-${item.id}`}>
                  {fmtBase(item.calculatedProfit, item.internalCurrency || base)}
                  {item.calculatedProfitPercent != null && (
                    <span className="muted"> ({Number(item.calculatedProfitPercent).toFixed(1)}%)</span>
                  )}
                </div>
              </div>
              <div className="fp-pricing-cell">
                <div className="fp-pricing-cell-label">Client Amount</div>
                <div className="fp-pricing-cell-value" data-testid={`fp-pricing-client-${item.id}`}>
                  {fmtBase(item.clientVisibleAmount, item.internalCurrency || base)}
                </div>
              </div>
              <div className="fp-pricing-cell">
                <div className="fp-pricing-cell-label">Supervision</div>
                <div className="fp-pricing-cell-value" data-testid={`fp-pricing-sup-${item.id}`}>
                  {fmtBase(item.supervisionAmount, item.internalCurrency || base)}
                </div>
              </div>
              <div className="fp-pricing-cell fp-pricing-cell-total">
                <div className="fp-pricing-cell-label">Client Total</div>
                <div className="fp-pricing-cell-value" data-testid={`fp-pricing-total-${item.id}`}>
                  {fmtBase(item.clientTotalAmount, item.internalCurrency || base)}
                </div>
              </div>
            </div>
            {item.linkedBoardTaskId && (
              <div className="fp-pricing-linked-hint">
                Linked to board task — Board will only see the <strong>Client Total</strong>, never the cost or profit.
              </div>
            )}
          </div>

          <div className="fp-pay-block fp-pay-block-sub" data-testid={`fp-sub-list-${item.id}`}>
            <div className="fp-pay-block-header">
              <div className="fp-pay-block-title">Subcontractor Payments (Outgoing)</div>
              <div className="fp-pay-block-stats">
                <span className="muted">Paid:</span> <strong>{fmtBase(item.subcontractorPaidBase, base)}</strong>
                <span className="fp-pay-block-sep">•</span>
                <span className="muted">Remaining cost:</span> <strong>{fmtBase(item.costRemainingBase, base)}</strong>
                <span className="fp-pay-block-sep">•</span>
                <span className="muted">Client billed so far:</span> <strong>{fmtBase(item.clientBilledBase, base)}</strong>
                <button
                  type="button"
                  className="fp-pill-btn fp-pill-btn-sub"
                  onClick={() => onAddSubPayment(item)}
                  data-testid={`fp-sub-list-${item.id}-add`}
                >
                  <FiPlus size={12} /> Add Payment
                </button>
              </div>
            </div>
            {subPayments.length === 0 ? (
              <div className="fp-pay-empty">No subcontractor payments yet.</div>
            ) : (
              <div className="fp-pay-rows">
                <div className="fp-pay-rows-head">
                  <div>Paid to Sub</div>
                  <div>Client-Equivalent</div>
                  <div>Date</div>
                  <div>By</div>
                  <div>Note</div>
                  <div></div>
                </div>
                {subPayments.map((p) => (
                  <div className="fp-pay-row fp-pay-row-with-equiv" key={p.id} data-testid={`fp-sub-list-${item.id}-row-${p.id}`}>
                    <div className="fp-pay-amount fp-pay-amount-sub">
                      {fmtMoney(p.amount)} <span className="fp-money-ccy">{p.currency}</span>
                    </div>
                    <div className="fp-pay-amount fp-pay-amount-cli">
                      → {fmtMoney(p.clientEquivalentAmount)} <span className="fp-money-ccy">{p.currency}</span>
                      {p.clientEquivalentOverridden && <span className="fp-money-locked">manual</span>}
                    </div>
                    <div className="fp-pay-date">{p.date}</div>
                    <div className="fp-pay-creator">
                      <span className="fp-pay-creator-dot" style={{ background: p.createdByColor || '#0F0F10' }} />
                      {p.createdByName || '—'}
                    </div>
                    <div className="fp-pay-note" title={p.note || ''}>{p.note || ''}</div>
                    <div className="fp-pay-actions">
                      {(p.createdBy === currentUserId || currentUserRole === 'Admin') && (
                        <>
                          <button type="button" className="icon-btn"
                            onClick={() => onEditSubPayment(p)} title="Edit"
                            data-testid={`fp-sub-list-${item.id}-edit-${p.id}`}>
                            <FiEdit2 size={12} />
                          </button>
                          <button type="button" className="icon-btn icon-btn-danger"
                            onClick={() => onDeletePayment(p.id)} title="Delete"
                            data-testid={`fp-sub-list-${item.id}-del-${p.id}`}>
                            <FiTrash2 size={12} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="fp-pay-block-footer">
              <span className="fp-realized-profit-label">Realized profit on this task:</span>{' '}
              <strong className={item.realizedProfitBase >= 0 ? 'fp-positive' : 'fp-negative'}>
                {fmtBase(item.realizedProfitBase, base)}
              </strong>{' '}
              <span className="muted">(client billed − sub paid)</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FinanceProjectDetail;
